import MonacoEditor, {
  useMonaco,
  loader,
  DiffEditor,
} from '@monaco-editor/react';
import type * as monaco from 'monaco-editor';
import React, { useRef, useEffect, useState, useCallback } from 'react';

import { cn } from '@/lib/utils';
import { ValidationError } from '@/types';

loader.config({ 'vs/nls': { availableLanguages: { '*': 'de' } } });

export enum MonacoLanguage {
  JSON = 'json',
  XML = 'xml',
}

interface MonacoJsonEditorProps {
  value: string;
  onChange?: (value: string) => void;
  /** When provided, a side-by-side diff is rendered (original = previousValue). */
  previousValue?: string;
  language?: MonacoLanguage;
  errors?: ValidationError[];
  className?: string;
  readOnly?: boolean;
  schema?: object;
  validateJson?: (jsonString: string) => void;
  height?: number;
  theme?: 'light' | 'dark';
}

export const MonacoJsonEditor: React.FC<MonacoJsonEditorProps> = ({
  value,
  onChange,
  previousValue,
  language = 'json',
  errors = [],
  className,
  readOnly = false,
  schema,
  height = 400,
  theme = 'light',
  validateJson,
}) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoInstance = useMonaco();
  const [isEditorReady, setIsEditorReady] = useState(false);

  const isDiff = previousValue !== undefined;

  // Configure Monaco JSON validation with the provided schema
  const configureJsonLanguage = useCallback(() => {
    if (monacoInstance && schema && language === 'json') {
      monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas: [
          {
            uri: 'http://opm-event-schema.json',
            fileMatch: ['*'],
            schema,
          },
        ],
      });
    }
  }, [schema, language, monacoInstance]);

  // Update Monaco markers for error highlighting
  const updateErrorMarkers = useCallback(() => {
    if (!editorRef.current || !monacoInstance || !isEditorReady) {
      return;
    }

    const model = editorRef.current.getModel();
    if (!model) {
      return;
    }

    monacoInstance.editor.setModelMarkers(model, 'json-validation', []);

    if (errors.length === 0) {
      return;
    }

    const markers: monaco.editor.IMarkerData[] = errors
      .filter((error) => error.line && error.line > 0)
      .map((error) => ({
        startLineNumber: error.line!,
        startColumn: error.column || 1,
        endLineNumber: error.line!,
        endColumn: error.column ? error.column + 10 : 1000,
        message: error.message,
        severity:
          error.severity === 'warning'
            ? monacoInstance.MarkerSeverity.Warning
            : error.severity === 'info'
              ? monacoInstance.MarkerSeverity.Info
              : monacoInstance.MarkerSeverity.Error,
      }));

    monacoInstance.editor.setModelMarkers(model, 'json-validation', markers);
  }, [errors, isEditorReady, monacoInstance]);

  // Suppress harmless Monaco cancellation errors that are thrown as unhandled promise rejections.
  // Monaco's JSON language service sometimes cancels background validation or parsing operations,
  // which results in "cancelation" errors or "operation is manually canceled" messages.
  // These are expected and not actual problems, but without this handler, they show up as
  // "Uncaught (in promise)" errors in the browser console, which can be confusing.
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (
        reason?.type === 'cancelation' ||
        reason?.msg === 'operation is manually canceled'
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  const handleValueChange = useCallback(
    (newValue: string) => {
      onChange?.(newValue);
      validateJson?.(newValue);
    },
    [onChange, validateJson],
  );

  // Shared setup once we have a concrete code editor (plain editor, or the
  // modified pane of the diff editor).
  const setupCodeEditor = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor) => {
      editorRef.current = editor;
      setIsEditorReady(true);

      configureJsonLanguage();

      if (monacoInstance) {
        editor.addAction({
          id: 'format-json',
          label: 'Format JSON',
          keybindings: [
            monacoInstance.KeyMod.Alt |
              monacoInstance.KeyMod.Shift |
              monacoInstance.KeyCode.KeyF,
          ],
          run: () => {
            editor.trigger('format', 'editor.action.formatDocument', {});
          },
        });
      }
    },
    [configureJsonLanguage, monacoInstance],
  );

  const handleEditorDidMount = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor) => {
      setupCodeEditor(editor);
    },
    [setupCodeEditor],
  );

  const handleDiffEditorDidMount = useCallback(
    (diffEditor: monaco.editor.IStandaloneDiffEditor) => {
      const modifiedEditor = diffEditor.getModifiedEditor();
      setupCodeEditor(modifiedEditor);

      // DiffEditor has no onChange prop, so listen on the modified model.
      modifiedEditor.onDidChangeModelContent(() => {
        handleValueChange(modifiedEditor.getValue());
      });
    },
    [setupCodeEditor, handleValueChange],
  );

  // Update markers when monaco instance becomes available
  useEffect(() => {
    if (monacoInstance && isEditorReady) {
      updateErrorMarkers();
    }
  }, [monacoInstance, isEditorReady, updateErrorMarkers]);

  // Re-configure when schema changes
  useEffect(() => {
    if (isEditorReady) {
      configureJsonLanguage();
    }
  }, [isEditorReady, configureJsonLanguage]);

  const editorOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
    selectOnLineNumbers: true,
    roundedSelection: false,
    cursorStyle: 'line',
    readOnly,
    automaticLayout: true,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    lineNumbers: 'on',
    glyphMargin: true,
    folding: true,
    lineDecorationsWidth: 20,
    lineNumbersMinChars: 4,
    renderLineHighlight: 'all',
    contextmenu: true,
    bracketPairColorization: { enabled: true },
    guides: {
      bracketPairs: true,
      indentation: true,
    },
    suggest: {
      showKeywords: true,
      showSnippets: true,
    },
    quickSuggestions: {
      other: true,
      comments: false,
      strings: true,
    },
    formatOnPaste: true,
    formatOnType: true,
    // Ensure error highlighting is enabled
    renderValidationDecorations: 'on',
    showFoldingControls: 'always',
    // Ensure tooltips are properly positioned
    fixedOverflowWidgets: true,
  };

  const diffEditorOptions: monaco.editor.IStandaloneDiffEditorConstructionOptions =
    {
      ...editorOptions,
      originalEditable: false,
    };

  return (
    <div
      className={cn(
        'border border-gray-300 rounded-md overflow-hidden',
        className,
      )}
    >
      {isDiff ? (
        <DiffEditor
          height={`${height}px`}
          language={language}
          theme={theme === 'dark' ? 'vs-dark' : 'vs'}
          original={previousValue}
          modified={value}
          options={diffEditorOptions}
          onMount={handleDiffEditorDidMount}
          keepCurrentModifiedModel
          keepCurrentOriginalModel
        />
      ) : (
        <MonacoEditor
          height={`${height}px`}
          language={language}
          theme={theme === 'dark' ? 'vs-dark' : 'vs'}
          value={value}
          options={editorOptions}
          onChange={(newValue) => handleValueChange(newValue ?? '')}
          onMount={handleEditorDidMount}
        />
      )}

      {errors.length > 0 && (
        <div className="border-t border-gray-200 bg-red-50 p-2 text-sm">
          <div className="font-medium text-red-800">
            {errors.length} validation error
            {errors.length !== 1 ? 's' : ''}:
          </div>
          <ul className="mt-1 space-y-1">
            {errors.slice(0, 5).map((error, index) => (
              <li key={index} className="text-red-700">
                {error.line && `Line ${error.line}: `}
                {error.message}
              </li>
            ))}
            {errors.length > 5 && (
              <li className="text-red-600 italic">
                ... and {errors.length - 5} more errors
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
